import React, { useEffect, useMemo, useState } from 'react';
import {
  backupContractScopeToLocalStorage,
  fetchDeliveryContractScope,
  polesContractStorageScope,
  saveDeliveryContractScopeState
} from '../services/deliveryContractQtyService';
import { parsePoItemsField } from '../services/purchaseOrdersService';
import { suppliersMatch } from '../utils/supplierNameNormalize';
import './PolesPoQuantitiesTable.css';

/** Wooden poles qty columns (aligned with Delivery Tracking / Purchase Order item names). */
export const POLES_PO_QTY_COLUMNS = [
  { display: '9M Medium', match: (h) => /\b9\s*m\b/.test(h) && h.includes('medium') },
  { display: '10M Medium', match: (h) => /\b10\s*m\b/.test(h) && h.includes('medium') },
  { display: '10M Stout', match: (h) => /\b10\s*m\b/.test(h) && h.includes('stout') },
  { display: '13M Medium', match: (h) => /\b13\s*m\b/.test(h) && h.includes('medium') },
  { display: '13M Stout', match: (h) => /\b13\s*m\b/.test(h) && h.includes('stout') },
  { display: '12M Medium', match: (h) => /\b12\s*m\b/.test(h) && h.includes('medium') },
  { display: '12M Stout', match: (h) => /\b12\s*m\b/.test(h) && h.includes('stout') }
];

const parseQty = (raw) => {
  if (raw === null || raw === undefined || raw === '' || raw === '-') return 0;
  const num = Number(String(raw).replace(/,/g, '').trim());
  return Number.isFinite(num) ? num : 0;
};

const normalizeItems = (itemsValue) => parsePoItemsField(itemsValue);

export const isWoodenPolesPurchaseOrder = (po) => {
  const category = String(po?.category || '').toLowerCase();
  if (category && category !== 'material') return false;

  const subcategory = String(po?.subcategory || '').toLowerCase().trim();
  const description = String(po?.description || '').toLowerCase();
  const items = normalizeItems(po?.items);
  const hasConcreteItem = items.some((item) => {
    const name = String(item?.itemName || item?.name || item?.description || '').toLowerCase();
    return name.includes('concrete');
  });
  if (subcategory.includes('concrete') || description.includes('concrete') || hasConcreteItem) {
    return false;
  }

  // Everwell supplies concrete poles only — never include in wooden poles qty table.
  const supplier = String(po?.supplierName || '').toLowerCase();
  if (supplier.includes('everwell')) return false;

  if (
    subcategory === 'poles' ||
    subcategory === 'wooden poles' ||
    subcategory === 'wooden pole' ||
    (subcategory.includes('pole') && !subcategory.includes('concrete'))
  ) {
    return true;
  }

  // Fallback: Material POs whose line items look like wooden poles
  return items.some((item) => {
    const name = String(item?.itemName || item?.name || item?.description || '').toLowerCase();
    return name.includes('pole') && !name.includes('concrete');
  });
};

const poMatchesLotTab = (po, lotTab) => {
  const lot = String(po?.lot || '');
  if (/\blot[\s\-_]*8\b/i.test(lotTab)) return /\blot[\s\-_]*8\b/i.test(lot);
  if (/\blot[\s\-_]*10\b/i.test(lotTab)) return /\blot[\s\-_]*10\b/i.test(lot);
  return lot.trim() === String(lotTab || '').trim();
};

const matchBoqColumn = (itemName) => {
  const hl = String(itemName || '')
    .toLowerCase()
    .replace(/[()]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return POLES_PO_QTY_COLUMNS.find((col) => col.match(hl))?.display || null;
};

const formatCell = (n) => {
  if (!n) return '';
  return Number(n).toLocaleString('en-US');
};

const formatFooter = (n) => Number(n || 0).toLocaleString('en-US');

const contractProgressPercent = (contractRaw, deliveredNum) => {
  const contract = parseQty(contractRaw);
  const delivered = parseQty(deliveredNum);
  if (contract <= 0) return null;
  return Math.round((delivered / contract) * 100);
};

const renderContractProgress = (contractRaw, deliveredNum) => {
  const pct = contractProgressPercent(contractRaw, deliveredNum);
  if (pct === null) {
    return <span className="ppq-progress__empty">—</span>;
  }
  const fillPct = Math.min(100, Math.max(0, pct));
  const over = pct > 100;
  return (
    <div className="ppq-progress">
      <div
        className="ppq-progress__track"
        role="progressbar"
        aria-valuenow={fillPct}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`${pct}% of contract delivered`}
      >
        <div
          className={`ppq-progress__fill${
            pct >= 100 ? ' ppq-progress__fill--complete' : ''
          }${over ? ' ppq-progress__fill--over' : ''}`}
          style={{ width: `${fillPct}%` }}
        />
      </div>
      <span className={`ppq-progress__label${over ? ' ppq-progress__label--over' : ''}`}>
        {pct}%
      </span>
    </div>
  );
};

const emptyQtys = () =>
  Object.fromEntries(POLES_PO_QTY_COLUMNS.map((c) => [c.display, 0]));

/**
 * Poles PO Quantities table:
 * - Body + Totals → Purchase Order tracker (supplier / PO / item qtys)
 * - Total Delivered → Poles tracker hamlet Delivered `(D)` columns for this LOT
 * - Balance → PO ordered − Total Delivered
 */
function PolesPoQuantitiesTable({
  lotTab,
  purchaseOrders = [],
  polesRows = [],
  editableValues = {},
  getPolesRowKey,
  readOnly = false
}) {
  const [supplierFilter, setSupplierFilter] = useState('all');
  const [contractQtys, setContractQtys] = useState({});
  const [contractScopeLoading, setContractScopeLoading] = useState(true);
  const [contractSaveState, setContractSaveState] = useState('idle');
  const [contractSaveError, setContractSaveError] = useState('');
  const storageScope = polesContractStorageScope(lotTab);

  useEffect(() => {
    setSupplierFilter('all');
  }, [lotTab]);

  useEffect(() => {
    let cancelled = false;
    setContractScopeLoading(true);
    (async () => {
      const state = await fetchDeliveryContractScope(storageScope);
      if (cancelled) return;
      setContractQtys(state.contractQtys || {});
      setContractScopeLoading(false);
      setContractSaveState('idle');
      setContractSaveError('');
    })();
    return () => {
      cancelled = true;
    };
  }, [storageScope, lotTab]);

  const handleContractQtyChange = (columnKey, rawValue) => {
    if (readOnly || contractScopeLoading) return;
    const value = String(rawValue ?? '');
    setContractQtys((prev) => {
      const next = { ...prev, [columnKey]: value };
      setContractSaveState('idle');
      backupContractScopeToLocalStorage(storageScope, next, {});
      return next;
    });
  };

  const handleSaveContractQtys = async () => {
    if (readOnly || contractScopeLoading) return;
    setContractSaveState('saving');
    setContractSaveError('');
    try {
      await saveDeliveryContractScopeState(storageScope, contractQtys, {});
      setContractSaveState('saved');
      window.setTimeout(() => setContractSaveState('idle'), 2500);
    } catch (err) {
      console.error('saveDeliveryContractScopeState failed:', err);
      setContractSaveState('error');
      setContractSaveError(
        err?.message || 'Could not save to Appwrite. Check your connection and try again.'
      );
    }
  };

  const contractSaveButtonLabel =
    contractSaveState === 'saving'
      ? 'Saving…'
      : contractSaveState === 'saved'
        ? 'Saved'
        : 'Save contract qty';

  const contractSaveBar = (
    <div className="ppq-contract-save-bar">
      <span className="ppq-contract-save-hint">
        {readOnly
          ? 'View-only account — contract qty is read-only.'
          : 'Edit contract qty in the yellow row below, then save for this LOT.'}
        {contractScopeLoading ? ' Loading…' : ''}
      </span>
      <div className="ppq-contract-save">
        <button
          type="button"
          className="ppq-contract-save-btn"
          onClick={handleSaveContractQtys}
          disabled={readOnly || contractScopeLoading || contractSaveState === 'saving'}
        >
          {contractSaveButtonLabel}
        </button>
        {contractSaveState === 'error' && contractSaveError ? (
          <span className="ppq-contract-save-error" role="alert">
            {contractSaveError}
          </span>
        ) : null}
      </div>
    </div>
  );

  /** One row per poles PO from the Purchase Order tracker. */
  const poRows = useMemo(() => {
    return (purchaseOrders || [])
      .filter((po) => poMatchesLotTab(po, lotTab))
      .map((po) => {
        const qtys = emptyQtys();
        normalizeItems(po.items).forEach((item) => {
          const col = matchBoqColumn(item?.itemName || item?.name || item?.description);
          if (!col) return;
          qtys[col] += parseQty(item?.qty ?? item?.quantity ?? item?.Qty);
        });
        return {
          id: po.id,
          supplier: String(po.supplierName || '').trim() || '-',
          poNumber: String(po.poNumber || '').trim() || '-',
          qtys
        };
      })
      .sort((a, b) => {
        const bySupplier = a.supplier.localeCompare(b.supplier);
        if (bySupplier !== 0) return bySupplier;
        return a.poNumber.localeCompare(b.poNumber);
      });
  }, [purchaseOrders, lotTab]);

  const supplierOptions = useMemo(() => {
    const names = poRows
      .map((row) => row.supplier)
      .filter((name) => name && name !== '-');
    return Array.from(new Set(names)).sort((a, b) => a.localeCompare(b));
  }, [poRows]);

  useEffect(() => {
    if (supplierFilter === 'all') return;
    if (!supplierOptions.includes(supplierFilter)) {
      setSupplierFilter('all');
    }
  }, [supplierFilter, supplierOptions]);

  const filteredPoRows = useMemo(() => {
    if (supplierFilter === 'all') return poRows;
    return poRows.filter((row) => suppliersMatch(row.supplier, supplierFilter));
  }, [poRows, supplierFilter]);

  /** Totals from Purchase Order tracker rows (respects supplier filter). */
  const poTotals = useMemo(() => {
    const totals = emptyQtys();
    filteredPoRows.forEach((row) => {
      POLES_PO_QTY_COLUMNS.forEach((c) => {
        totals[c.display] += row.qtys[c.display] || 0;
      });
    });
    return totals;
  }, [filteredPoRows]);

  /** Total Delivered from Poles Tracking (hamlet) Delivered columns for this LOT. */
  const deliveredTotals = useMemo(() => {
    const totals = emptyQtys();
    polesRows.forEach((row) => {
      const rowKey = typeof getPolesRowKey === 'function' ? getPolesRowKey(row) : '';
      if (!rowKey) return;
      const edits = editableValues[rowKey] || {};
      if (supplierFilter !== 'all') {
        // Match PO filter name to poles SUPPLIER (e.g. "Sunbird Forestry Limited" ↔ "Sunbird")
        if (!suppliersMatch(edits.SUPPLIER || '', supplierFilter)) return;
      }
      POLES_PO_QTY_COLUMNS.forEach((c) => {
        totals[c.display] += parseQty(edits[`${c.display} (D)`]);
      });
    });
    return totals;
  }, [polesRows, editableValues, getPolesRowKey, supplierFilter]);

  const contractBalanceTotals = useMemo(() => {
    const balances = {};
    POLES_PO_QTY_COLUMNS.forEach((c) => {
      const contract = parseQty(contractQtys[c.display]);
      balances[c.display] = contract - (deliveredTotals[c.display] || 0);
    });
    return balances;
  }, [contractQtys, deliveredTotals]);

  return (
    <div className="ppq-embed">
      <div className="ppq-embed-title-bar">
        <div className="ppq-embed-title-group">
          <span className="ppq-embed-title">Poles PO Quantities</span>
          <label className="ppq-supplier-filter" title="Filter PO rows by supplier">
            Supplier
            <select
              value={supplierFilter}
              onChange={(e) => setSupplierFilter(e.target.value)}
              className="ppq-supplier-filter-select"
            >
              <option value="all">All suppliers</option>
              {supplierOptions.map((supplier) => (
                <option key={supplier} value={supplier}>
                  {supplier}
                </option>
              ))}
            </select>
          </label>
        </div>
        <span className="ppq-embed-lot">{lotTab || '—'}</span>
      </div>
      {contractSaveBar}
      <div className="ppq-table-wrap">
        <table className="ppq-table">
          <thead>
            <tr>
              <th>Supplier</th>
              <th>PO No.</th>
              {POLES_PO_QTY_COLUMNS.map((col) => (
                <th key={col.display}>{col.display}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredPoRows.length === 0 ? (
              <tr>
                <td colSpan={2 + POLES_PO_QTY_COLUMNS.length} className="ppq-empty">
                  {poRows.length === 0
                    ? 'No poles purchase orders for this LOT in Purchase Order Tracking.'
                    : `No poles purchase orders for supplier “${supplierFilter}” on this LOT.`}
                </td>
              </tr>
            ) : (
              filteredPoRows.map((row) => (
                <tr key={row.id}>
                  <td>{row.supplier}</td>
                  <td>
                    {row.poNumber && row.poNumber !== '-'
                      ? `PO ${String(row.poNumber).replace(/^PO\s*/i, '')}`
                      : '—'}
                  </td>
                  {POLES_PO_QTY_COLUMNS.map((col) => (
                    <td key={col.display} className="ppq-num">
                      {formatCell(row.qtys[col.display])}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
          <tfoot>
            <tr className="ppq-row-totals">
              <td colSpan={2}>
                Totals (Purchase Orders)
                {supplierFilter !== 'all' ? ` · ${supplierFilter}` : ''}
              </td>
              {POLES_PO_QTY_COLUMNS.map((col) => (
                <td key={col.display} className="ppq-num">
                  {formatFooter(poTotals[col.display])}
                </td>
              ))}
            </tr>
            <tr className="ppq-row-contract">
              <td colSpan={2}>
                <div className="ppq-contract-label-row">
                  <span>Contract qty</span>
                  <button
                    type="button"
                    className="ppq-contract-save-btn ppq-contract-save-btn--inline"
                    onClick={handleSaveContractQtys}
                    disabled={readOnly || contractScopeLoading || contractSaveState === 'saving'}
                  >
                    {contractSaveButtonLabel}
                  </button>
                </div>
              </td>
              {POLES_PO_QTY_COLUMNS.map((col) => (
                <td key={col.display} className="ppq-num">
                  <input
                    type="text"
                    inputMode="decimal"
                    className="ppq-contract-input"
                    value={contractQtys[col.display] ?? ''}
                    onChange={(e) => handleContractQtyChange(col.display, e.target.value)}
                    readOnly={readOnly || contractScopeLoading}
                    disabled={readOnly || contractScopeLoading}
                    aria-label={`Contract qty ${col.display}`}
                  />
                </td>
              ))}
            </tr>
            <tr className="ppq-row-progress">
              <td colSpan={2}>Progress (delivered ÷ contract)</td>
              {POLES_PO_QTY_COLUMNS.map((col) => (
                <td key={col.display} className="ppq-progress-cell">
                  {renderContractProgress(contractQtys[col.display], deliveredTotals[col.display])}
                </td>
              ))}
            </tr>
            <tr className="ppq-row-delivered">
              <td colSpan={2}>
                Total qty delivered (Poles Tracker)
                {supplierFilter !== 'all' ? ` · ${supplierFilter}` : ''}
              </td>
              {POLES_PO_QTY_COLUMNS.map((col) => (
                <td key={col.display} className="ppq-num">
                  {formatFooter(deliveredTotals[col.display])}
                </td>
              ))}
            </tr>
            <tr className="ppq-row-balance">
              <td colSpan={2}>Contract balance (Contract − Delivered)</td>
              {POLES_PO_QTY_COLUMNS.map((col) => (
                <td key={col.display} className="ppq-num">
                  {formatFooter(contractBalanceTotals[col.display])}
                </td>
              ))}
            </tr>
          </tfoot>
        </table>
      </div>
      <div className="ppq-footnote">
        PO totals come from Purchase Order Tracking. Enter contract qty, then Save contract qty
        (Appwrite){contractScopeLoading ? ' (loading…)' : ''}.
        Total delivered comes from Poles hamlet Delivered columns
        {supplierFilter !== 'all' ? ` (filtered to ${supplierFilter})` : ''}.
      </div>
    </div>
  );
}

export default PolesPoQuantitiesTable;
